import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    PLATFORM_ID,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ApiTypes,
    futureDateValidator,
    numericValidator,
    ServicesFeature,
    TrackingFeature,
    TrackingPageElementStatusType,
} from '@hyundai/ng-common-lib';
import { DISTRIBUTIONTABS, distributionTabs, IttCartItem } from '@hyundai/shared-lib';
import { M040C140TabsComponent } from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { CHECKOUT_STEP_TOKEN, CheckoutStepComponent } from '@pixelgenau/ngx-checkouts';
import { format } from 'date-fns/format';
import DistributionGeneralRequestStepComponent from '../distribution-generalrequest-step/distribution-generalrequest-step.component';
import DistributionReservationStepComponent from '../distribution-reservation-step/distribution-reservation-step.component';
import DistributionTestDriveStepComponent from '../distribution-testdrive-step/distribution-testdrive-step.component';

export type ReservationStepData = Pick<
    ApiTypes.ReservationFormPayload,
    | 'name'
    | 'email'
    | 'phoneNumber'
    | 'tradeIn'
    | 'disclaimer'
    | 'marketingEmail'
    | 'marketingPhone'
    | 'marketingSms'
    | 'captchaSolution'
    | 'accessories'
>;

export type GeneralRequestData = ReservationStepData & Pick<ApiTypes.GeneralRequestFormPayload, 'message'>;

export type TestDriveFormData = GeneralRequestData & Pick<ApiTypes.TestDriveFormPayload, 'wishDate' | 'wishTime'>;

export interface DistributionForm {
    tab: FormControl<DISTRIBUTIONTABS>;
    wishDate: FormControl<Date | null>;
    wishTime: FormControl<TestDriveFormData['wishTime'] | null>;
    message: FormControl<string | null>;
    name: FormControl<string>;
    email: FormControl<string>;
    phoneNumber: FormControl<string | null>;
    tradeIn: FormControl<boolean>;
    privacyNotice: FormControl<boolean>;
    marketingEmail: FormControl<boolean>;
    marketingPhone: FormControl<boolean>;
    marketingSms: FormControl<boolean>;
    captchaSolution: FormControl<string>;
}

@Component({
    selector: 'lib-distribution-overview-step',
    standalone: true,
    templateUrl: './distribution-overview-step.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        M040C140TabsComponent,
        DistributionTestDriveStepComponent,
        DistributionGeneralRequestStepComponent,
        DistributionReservationStepComponent,
    ],
    providers: [{ provide: CHECKOUT_STEP_TOKEN, useExisting: DistributionOverviewStepComponent }],
})
export default class DistributionOverviewStepComponent extends CheckoutStepComponent {
    store = inject(Store);
    route = inject(ActivatedRoute);
    router = inject(Router);
    servicesFormContent = this.store.selectSignal(ServicesFeature.state.selectLoadedServiceForm);
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    vehicle = input.required<ApiTypes.VehicleDetail>();
    trackingIndex = 0;

    accessories = computed(() => {
        // get cart items from session storage if platformIsBrowser

        if (this.isBrowser) {
            const cart = sessionStorage.getItem('cart');

            if (cart) {
                const cartItems = JSON.parse(cart) ?? [];
                return cartItems.map((item: IttCartItem) => {
                    return `${item.name} (${item.articleNumber}) ${item.price.value} ${item.price.currency}`;
                });
            }
        }

        return [];
    });

    tab: DISTRIBUTIONTABS = this.route.snapshot.queryParams['tab'];

    override form = new FormGroup<DistributionForm>({
        tab: new FormControl<DISTRIBUTIONTABS>(distributionTabs.includes(this.tab) ? this.tab : 'allg-anfrage', {
            nonNullable: true,
        }),
        wishTime: new FormControl<TestDriveFormData['wishTime'] | null>(null, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        wishDate: new FormControl<Date | null>(null, { validators: [Validators.required, futureDateValidator()] }),
        message: new FormControl<string | null>(null),
        name: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
        email: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required, Validators.email],
        }),
        phoneNumber: new FormControl<string | null>(null, { validators: [numericValidator()] }),
        tradeIn: new FormControl<boolean>(false, { nonNullable: true }),
        privacyNotice: new FormControl<boolean>(false, {
            nonNullable: true,
            validators: [],
        }),
        marketingEmail: new FormControl<boolean>(false, { nonNullable: true }),
        marketingPhone: new FormControl<boolean>(false, { nonNullable: true }),
        marketingSms: new FormControl<boolean>(false, { nonNullable: true }),
        captchaSolution: new FormControl<string>('', {
            nonNullable: true,
            validators: [Validators.required],
        }),
    });

    // eslint-disable-next-line max-lines-per-function
    constructor() {
        super();
        // eslint-disable-next-line max-statements, max-lines-per-function
        this.form.controls.tab.valueChanges.pipe(takeUntilDestroyed()).subscribe((tab) => {
            let i: undefined | number;
            let label: undefined | string;
            if (tab === 'probefahrt') {
                this.form.controls.wishDate.enable();
                this.form.controls.wishTime.enable();
                this.form.controls.message.clearValidators();
                this.form.controls.message.enable();
                this.router.navigate([], {
                    queryParams: { tab: 'probefahrt' },
                });
                i = 1;
                label = 'Test Drive';
            } else if (tab === 'reservierung') {
                this.form.controls.wishDate.disable();
                this.form.controls.wishTime.disable();
                this.form.controls.message.clearValidators();
                this.form.controls.message.disable();
                i = 3;
                label = 'Offer';
                this.router.navigate([], {
                    queryParams: { tab: 'reservierung' },
                });
            } else {
                // tab === 'allg-anfrage'
                this.form.controls.wishDate.disable();
                this.form.controls.wishTime.disable();
                this.form.controls.message.addValidators([Validators.required]);
                this.form.controls.message.enable();
                i = 2;
                label = 'General Request';
                this.router.navigate([], {
                    queryParams: { tab: 'allg-anfrage' },
                });
            }
            if (this.trackingIndex > 0) {
                this.store.dispatch(
                    TrackingFeature.actions.clickTracking({
                        elType: 'tab',
                        elLabel: label,
                        elTarget: 'tab',
                        event: 'element_change',
                        status: `tab_${i}` as TrackingPageElementStatusType,
                    }),
                );
            }
            this.trackingIndex++;
        });
    }

    next(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        super.complete();
    }

    override showStepperMenu(): boolean {
        return false;
    }

    override showSidebar(): boolean {
        return true;
    }

    override getSubmitData(): ReservationStepData | GeneralRequestData | TestDriveFormData {
        const tab = this.form.value.tab;

        const ret: ReservationStepData | GeneralRequestData | TestDriveFormData = {
            name: this.form.value.name ?? '',
            email: this.form.value.email ?? '',
            phoneNumber: this.form.value.phoneNumber ?? '',
            tradeIn: this.form.value.tradeIn ?? false,
            marketingEmail: this.form.value.marketingEmail ?? false,
            marketingPhone: this.form.value.marketingPhone ?? false,
            marketingSms: this.form.value.marketingSms ?? false,
            disclaimer: true,
            captchaSolution: this.form.value.captchaSolution ?? '',
            accessories: this.accessories(),
        };

        if (tab === 'probefahrt') {
            return {
                ...ret,
                wishDate: this.form.value.wishDate ? format(this.form.value.wishDate, 'yyyy-MM-dd') : '',
                wishTime: this.form.value.wishTime ?? 'Beliebige Tageszeit',
                message: this.form.value.message ?? '',
            } satisfies TestDriveFormData;
        } else if (tab === 'allg-anfrage') {
            return {
                ...ret,
                message: this.form.value.message ?? '',
            };
        }

        return ret;
    }
}
